import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { getUserInfo, logout } from '../lib/auth';
import { getConfig } from '../lib/config';
import { listAppUsers, listScanAnalysisQueue } from '../lib/api';
import { theme } from '../theme';

interface NavItem {
  to: string;
  label: string;
  /** Optional dynamic count rendered as a badge after the label. Hidden when 0/undefined. */
  badgeKey?: 'appUsersPending' | 'analysisReviewPending';
}

const NAV_ITEMS: NavItem[] = [
  { to: '/scans', label: 'Recent Scans' },
  { to: '/failures', label: 'Failures' },
  { to: '/app-users', label: 'App Users', badgeKey: 'appUsersPending' },
  { to: '/scan-analysis-queue', label: 'Analysis Review', badgeKey: 'analysisReviewPending' },
  { to: '/kb', label: 'Vehicle KB' },
  { to: '/aggregations', label: 'Aggregations' },
];

// Refresh every 60s so badge counts feel live without hammering the API.
// On mount we fetch immediately so the user doesn't wait a minute for
// counts to appear.
const COUNTS_REFRESH_MS = 60_000;

export function AppShell() {
  const user = getUserInfo();
  const { environment, buildCommit } = getConfig();
  const envBadge =
    environment === 'production' ? null : (
      <span style={styles.envBadge}>{environment.toUpperCase()}</span>
    );

  const [counts, setCounts] = useState<{
    appUsersPending: number;
    analysisReviewPending: number;
  }>({ appUsersPending: 0, analysisReviewPending: 0 });

  useEffect(() => {
    // Initial fetch + interval. allSettled so a failure on one count
    // doesn't suppress the others; any count can stay at 0 (no badge
    // shown) without blocking the rest of the UI.
    let cancelled = false;
    const refresh = async (): Promise<void> => {
      const [users, queue] = await Promise.allSettled([
        listAppUsers({ status: 'pending-approval', limit: 100 }),
        listScanAnalysisQueue({ limit: 100 }),
      ]);
      if (cancelled) return;
      setCounts({
        appUsersPending: users.status === 'fulfilled' ? users.value.users.length : 0,
        analysisReviewPending:
          queue.status === 'fulfilled'
            ? queue.value.items.filter(
                (i) =>
                  i.review.status === 'pending-review' || i.review.status === 'analysis-failed',
              ).length
            : 0,
      });
    };
    void refresh();
    const id = setInterval(() => {
      void refresh();
    }, COUNTS_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const displayLabel =
    user?.givenName || user?.familyName
      ? `${user.givenName} ${user.familyName}`.trim()
      : (user?.email ?? '');

  return (
    <div style={styles.shell}>
      <header style={styles.header}>
        <div style={styles.brandWrap}>
          <img
            src="/tradebid-logo-white.svg"
            alt="TradeBid"
            style={styles.brandLogo}
            width={140}
            height={21}
          />
          <span style={styles.brandSeparator} aria-hidden="true">
            ·
          </span>
          <span style={styles.brandSubtitle}>OBD Reader</span>
          {envBadge}
        </div>
        <nav style={styles.nav}>
          {NAV_ITEMS.map((item) => {
            const count = item.badgeKey ? counts[item.badgeKey] : 0;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                style={({ isActive }) => ({
                  ...styles.navLink,
                  ...(isActive ? styles.navLinkActive : {}),
                })}
              >
                {item.label}
                {count > 0 && <span style={styles.navCountBadge}>{count}</span>}
              </NavLink>
            );
          })}
        </nav>
        <div style={styles.user}>
          {user && (
            <>
              <span style={styles.userEmail} title={user.email}>
                {displayLabel}
              </span>
              <button onClick={logout} style={styles.logoutBtn}>
                Sign out
              </button>
            </>
          )}
        </div>
      </header>
      <main style={styles.main}>
        <Outlet />
      </main>
      <footer style={styles.footer}>
        TradeBid Auctions · build {buildCommit.slice(0, 7)} · env {environment}
      </footer>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  shell: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  // Dark band + cyan accent line — mirrors the brandBand in the PDF
  // renderer (Report.tsx brandBand) so PDF and dashboard headers feel
  // visually identical.
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '14px 28px',
    background: theme.surface.header,
    borderBottom: `2px solid ${theme.brand.primary}`,
  },
  brandWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  brandLogo: {
    height: 22,
    width: 'auto',
    display: 'block',
  },
  brandSeparator: {
    color: theme.text.tertiary,
    fontSize: 16,
    lineHeight: 1,
  },
  brandSubtitle: {
    color: theme.text.secondary,
    fontSize: 14,
    fontWeight: 500,
    letterSpacing: 0.2,
  },
  envBadge: {
    marginLeft: 8,
    padding: '2px 8px',
    background: theme.status.warning,
    color: theme.text.onBrand,
    borderRadius: theme.radius.sm,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
  },
  nav: {
    display: 'flex',
    gap: 4,
    marginLeft: 24,
    flex: 1,
  },
  navLink: {
    padding: '6px 12px',
    color: theme.text.secondary,
    borderRadius: theme.radius.md,
    fontSize: 14,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },
  navLinkActive: {
    background: theme.surface.panel,
    color: theme.brand.primary,
  },
  navCountBadge: {
    background: theme.status.warning,
    color: theme.text.onBrand,
    padding: '1px 7px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    lineHeight: '16px',
    minWidth: 16,
    textAlign: 'center' as const,
  },
  user: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  userEmail: {
    color: theme.text.secondary,
    fontSize: 13,
  },
  logoutBtn: {
    background: 'transparent',
    border: `1px solid ${theme.surface.borderStrong}`,
    color: theme.text.secondary,
    padding: '6px 12px',
    borderRadius: theme.radius.md,
    fontSize: 13,
  },
  main: {
    flex: 1,
    padding: '24px',
    maxWidth: 1400,
    margin: '0 auto',
    width: '100%',
  },
  footer: {
    padding: '12px 28px',
    color: theme.text.tertiary,
    fontSize: 12,
    borderTop: `1px solid ${theme.surface.border}`,
  },
};
